import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from '../../../../Flux/index.js'
import { cubeStateKey, visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { AppDispatch } from '../../../../Flux/index.js'
import type { SavedViz } from '../Persistence/SavedViz.js'
import { clearPreviousAutosave, loadPreviousAutosave, rotateAutosaveToPrevious } from '../Persistence/StorageNext.js'
import { defaultWorkspace } from './DefaultWorkspaceConfiguration.js'

export type ResumableSession = { savedAt: number; resume: () => void; dismiss: () => void }

function applySaved(dispatch: AppDispatch, saved: SavedViz, asTemplate: boolean) {
  dispatch(visualizationSlice.actions.setRestoring({ restoring: true }))
  dispatch(visualizationSlice.actions.loadSavedViz({ type: 'LoadSavedVizAction', saved, asTemplate }))
  setTimeout(() => dispatch(visualizationSlice.actions.setRestoring({ restoring: false })), 0)
}

/**
 * Fills the workspace once its scope's data is ready: a `?remoteView=` link if given, otherwise the default
 * layout, with the last worked-in session offered for restoring. Autosave stays held until the user edits, so
 * an untouched default never replaces that session in storage.
 */
export function useWorkspaceHydration(scopeId: string | undefined, ready: boolean, savedViewsAccess: boolean) {
  const dispatch = useDispatch()
  const location = useLocation()
  const hydratedForRef = useRef<string | null>(null)
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  const [previous, setPrevious] = useState<{ saved: SavedViz; savedAt: number } | null>(null)
  const warmedForRef = useRef<string | null>(null)

  // The cube depends on the scope id alone, so fetch the default layout's while the scope's own data is
  // still loading rather than after the source is built from it.
  useEffect(() => {
    if (!scopeId || warmedForRef.current === scopeId) return
    warmedForRef.current = scopeId
    if (new URLSearchParams(location.search).get('remoteView')) return
    const requested = new Set<string>()
    for (const { shared } of Object.values(defaultWorkspace.sources)) {
      const key = cubeStateKey(scopeId, shared.blastType, shared.spin)
      if (requested.has(key)) continue
      requested.add(key)
      dispatch(visualizationSlice.actions.cubeRequested({ scopeId, blastType: shared.blastType, spin: shared.spin }))
    }
  }, [scopeId, location.search, dispatch])

  useEffect(() => {
    if (!ready || !scopeId || hydratedForRef.current === scopeId) return
    hydratedForRef.current = scopeId
    setHydratedFor(scopeId)
    setPrevious(null)

    const search = new URLSearchParams(location.search)
    const remoteId = search.get('remoteView')
    if (remoteId && savedViewsAccess) {
      dispatch(visualizationSlice.actions.setRestoring({ restoring: true }))
      dispatch(visualizationSlice.actions.remoteLoadRequest({ id: remoteId, asTemplate: search.get('asTemplate') === '1' }))
      return
    }

    applySaved(dispatch, defaultWorkspace, true)
    dispatch(visualizationSlice.actions.holdAutosave())
    rotateAutosaveToPrevious(scopeId)
    setPrevious(loadPreviousAutosave(scopeId))
  }, [ready, scopeId, location.search, savedViewsAccess, dispatch])

  const resume = useCallback(() => {
    if (previous) applySaved(dispatch, previous.saved, false)
    setPrevious(null)
  }, [previous, dispatch])

  const dismiss = useCallback(() => {
    if (scopeId) clearPreviousAutosave(scopeId)
    setPrevious(null)
  }, [scopeId])

  const resumable: ResumableSession | null = previous ? { savedAt: previous.savedAt, resume, dismiss } : null

  return { hydrated: !!scopeId && hydratedFor === scopeId, resumable }
}
